import React, { useState } from 'react';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { NamedLink } from '../../components';
import css from './FAQPage.module.css';

// ─── FAQ Data ───

const FAQ_SECTIONS = [
  {
    title: 'General',
    items: [
      {
        q: 'What is Merrily?',
        a: 'Merrily is a peer-to-peer marketplace where you can rent beautiful party supplies, decorations, and themed kits from people in your neighborhood. Think of it as Airbnb for party supplies: borrow what you need, return it when you\'re done, and save money while reducing waste.',
      },
      {
        q: 'How does it work?',
        a: 'Browse party kits by theme or occasion, pick the one you love, select your dates, and request to book. The host has up to 72 hours to confirm, though most respond much sooner. You can arrange pickup or delivery, enjoy your party, then return everything clean and ready for the next celebration.',
      },
      {
        q: 'What areas do you serve?',
        a: 'Merrily is currently launching in Santa Barbara, CA. We\'re expanding to new cities soon! If you\'d like Merrily in your area, let us know.',
      },
      {
        q: 'Is there a minimum rental period?',
        a: 'Most kits have a minimum rental of 1 day. Some hosts offer multi-day discounts. Check each listing for specific details.',
      },
    ],
  },
  {
    title: 'Fees & Protection',
    slug: 'fees',
    items: [
      {
        q: 'What’s included in the price I see?',
        a: 'The price on every listing is all-in. It includes our 15% Service & Protection Fee, so you won’t see a separate fee line at checkout. The only thing that could be added is delivery, if the host offers it and you choose that option. Sales tax may apply depending on your location.',
      },
      {
        q: 'What is the Service & Protection Fee?',
        a: 'It’s a 15% fee baked into every booking that covers our platform costs and includes accidental-damage protection for both sides of the rental, up to 50% of the item’s replacement cost.',
      },
      {
        q: 'How does the security deposit work?',
        a: 'A refundable deposit (between $20 and $500, shown in your price breakdown before you pay) is charged with your rental at checkout. It is refunded automatically about 48 hours after you return the items clean and on time. If something is damaged or the return is late, part or all of the deposit can be kept to cover it.',
      },
      {
        q: 'How do I file a damage claim?',
        a: 'If items are damaged during your rental, the host can file a claim through their dashboard within 48 hours of the return. We’ll review the claim, assess the damage, and process the coverage. Both parties will be notified throughout the process.',
      },
      {
        q: 'What happens when there\u2019s a dispute?',
        a: 'If a host reports damage (within 48 hours of return, with photos), our team reviews the evidence and decides: refund the full deposit, keep part of it, or keep all of it. If verified accidental damage exceeds the deposit, Merrily\u2019s protection covers the gap up to 50% of the item\u2019s replacement cost. Both sides are notified of the outcome.',
      },
      {
        q: 'Are there any hidden fees?',
        a: 'No. The rental price you see already includes everything except optional delivery and the refundable security deposit, both shown clearly in your price breakdown before you pay. The deposit is charged with your rental and automatically refunded after a clean return.',
      },
    ],
  },
  {
    title: 'For Renters',
    items: [
      {
        q: 'What if I need to cancel?',
        a: 'You can cancel a booking before the host confirms it at no charge. After confirmation, our cancellation policy applies. Contact us if you have special circumstances. We\'re always happy to help.',
      },
      {
        q: 'What condition should items be in when I return them?',
        a: 'Items should be returned clean and in the same condition you received them. Normal wear from party use is expected. If something breaks, our protection coverage has you covered.',
      },
      {
        q: 'How do custom quotes work?',
        a: 'Some kits are quoted per event instead of priced per day. Tell the host about your event up front (date, guest count, pickup or delivery) and they\u2019ll send an itemized quote \u2014 usually within their 72-hour response window. You can accept and pay, or ask for changes; nothing is charged until you accept. The same cancellation policy applies as regular bookings.',
      },
      {
        q: 'What if the kit is dirty or incomplete at pickup?',
        a: 'Don\u2019t start the rental \u2014 tap \u201cFound an issue? Report it first\u201d on your booking page and document it with photos. Unresolved pickup issues qualify for a full refund, and reporting before you start protects you from being charged for problems that weren\u2019t your fault.',
      },
      {
        q: 'What happens if I return the kit late?',
        a: 'Talk to your host as soon as you know you\u2019re running behind \u2014 many are flexible. Late returns can be charged as an additional rental day, and the deposit isn\u2019t refunded until the host confirms the return, so lateness delays your money too.',
      },
      {
        q: 'Can I pick up and drop off, or is delivery available?',
        a: 'Each host sets their own service options: self-pickup, delivery, or full service (host delivers, sets up your kit at your event, then returns at the end to break down and remove everything). Pickup is always free; delivery and full service are priced per listing. Check the listing details for what each host offers.',
      },
    ],
  },
  {
    title: 'Cancellations',
    // Slug used as the section's anchor id (e.g. /faq#cancellation). Linked
    // to from the listing detail page's "Cancellation policy" link. Keep
    // this slug stable -- changing it will break those external anchors.
    slug: 'cancellation',
    items: [
      {
        q: 'How do cancellations work?',
        a: 'You can cancel a booking before the host confirms it at no charge. After the host confirms: cancel 7+ days before pickup for a full refund, cancel 3-7 days before and half the rental price is refunded, and within 3 days of pickup the rental price is non-refundable. Your deposit and any delivery fees always come back to you in full, whenever you cancel, and refunds are processed automatically. The same policy applies to every kit, including custom-quote bookings. Hosts can also cancel in rare cases (e.g. damaged kit) and you\'ll get a full refund.',
      },
      {
        q: 'What if my event is canceled or rescheduled?',
        a: 'Reach out to us at support@rentmerrily.com -- we work with hosts on rescheduling whenever possible, especially for life events outside your control. The standard policy applies if rescheduling isn\'t available.',
      },
      {
        q: 'Can the host cancel on me?',
        a: 'Hosts can cancel before pickup if a kit becomes unavailable (damage, prior renter delay, etc.). You\'ll get a full refund and we\'ll help you find a similar kit. Repeat cancellations affect the host\'s standing in the marketplace.',
      },
      {
        q: 'Can I change my service type after booking?',
        a: 'There\'s no in-app switcher, so message your host. If they have availability, they may be able to cancel and rebook with the new service. The standard cancellation policy above applies to the rebooking.',
      },
    ],
  },
  {
    title: 'For Hosts',
    items: [
      {
        q: 'How do I list my party supplies?',
        a: 'Click "List Your Kit" in the navigation, fill out the details about your kit (photos, description, pricing, available dates), and submit for review. Once approved, your listing goes live and renters can start booking.',
      },
      {
        q: 'How much can I earn?',
        a: 'Earnings depend on your kit\u2019s value, how often it rents, and your pricing \u2014 you keep 90% of every rental. The earnings calculator on our host signup page gives a realistic estimate for your category and rental pace.',
      },
      {
        q: 'When do I get paid?',
        a: 'Your payout starts the moment you confirm the kit\u2019s return \u2014 Stripe then deposits it to your bank account within 1\u20133 business days.',
      },
      {
        q: 'What about taxes?',
        a: 'You\u2019re responsible for taxes on your rental income \u2014 Merrily doesn\u2019t withhold anything. If your volume crosses IRS thresholds, you\u2019ll receive a Form 1099-K through Stripe. When in doubt, ask a tax professional.',
      },
      {
        q: 'What if a renter damages my items?',
        a: 'Report the damage from your dashboard within 48 hours of the return, with photos. The report flow walks you through it. The renter’s deposit is applied first; if verified accidental damage exceeds it, Merrily’s damage protection covers the gap up to 50% of the item’s replacement cost (one claim per booking; normal wear excluded). We review every claim and handle the money movement.',
      },
    ],
  },
];

// ─── Accordion Item ───

const AccordionItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={css.accordionItem}>
      <button
        type="button"
        className={css.accordionButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className={css.accordionQuestion}>{question}</span>
        <span className={css.accordionIcon}>{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && (
        <div className={css.accordionAnswer}>
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

// ─── FAQ Page ───

const FAQPage = () => {
  // FAQPage structured data (rich-result eligible). Built from the same
  // in-code Q&A list the page renders, so it can never drift from the
  // visible content; non-string answers (none today) are skipped rather
  // than serialized wrong.
  const faqSchema = {
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_SECTIONS.flatMap(section => section.items)
      .filter(item => typeof item.q === 'string' && typeof item.a === 'string')
      .map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
  };

  return (
    <Page
      title="FAQ"
      description="Frequently asked questions about Merrily: fees, protection coverage, how it works, and more."
      schema={faqSchema}
      className={css.root}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.container}>
          <div className={css.header}>
            <h1 className={css.title}>Frequently Asked Questions</h1>
            <p className={css.subtitle}>
              Everything you need to know about renting party supplies on Merrily.
            </p>
          </div>

          {FAQ_SECTIONS.map(section => (
            <div
              key={section.title}
              id={section.slug}
              className={css.section}
            >
              <h2 className={css.sectionTitle}>{section.title}</h2>
              <div className={css.accordionList}>
                {section.items.map(item => (
                  <AccordionItem
                    key={item.q}
                    question={item.q}
                    answer={item.a}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className={css.contactCta}>
            <h2 className={css.ctaTitle}>Still have questions?</h2>
            <p className={css.ctaText}>
              We're here to help! Reach out anytime.
            </p>
            <NamedLink name="ContactPage" className={css.ctaButton}>
              Contact Us
            </NamedLink>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default FAQPage;
